import { useEffect, useState } from 'react';
import { BarChart3, RefreshCw, CheckCircle2, AlertTriangle, Shield } from 'lucide-react';
import Card from '@/components/ui/Card';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';
import { cn } from '@/lib/utils';

interface ToolStat {
  tool_name: string;
  invocations: number;
  success_rate: number | null;
  p50_ms: number | null;
  p95_ms: number | null;
  last_used_at: string | null;
  last_failure_at: string | null;
  last_failure_output: any;
}

interface RecoveryStats {
  days: number;
  self_resolved: number;
  escalated: number;
  still_failing: number;
  terminal_total: number;
  self_resolved_rate: number | null;
}

const DAY_OPTIONS = [1, 7, 30, 90];

function rateColor(v: number | null): string {
  if (v == null) return 'text-stone-500';
  if (v >= 90) return 'text-green-700';
  if (v >= 75) return 'text-amber-700';
  return 'text-red-700';
}

function fmtRate(v: number | null): string {
  if (v == null) return '—';
  return `${Number(v).toFixed(1)}%`;
}

function fmtMs(v: number | null): string {
  if (v == null) return '—';
  if (v >= 1000) return `${(v / 1000).toFixed(1)}s`;
  return `${v}ms`;
}

function fmtRelative(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function fmtFailureOutput(output: any): string {
  if (!output) return '';
  if (typeof output === 'string') return output.slice(0, 200);
  const err = output.error || output.message || JSON.stringify(output);
  return typeof err === 'string' ? err.slice(0, 200) : '';
}

export default function AgentToolStats() {
  const addToast = useUIStore((s) => s.addToast);
  const [days, setDays] = useState(7);
  const [tools, setTools] = useState<ToolStat[] | null>(null);
  const [recovery, setRecovery] = useState<RecoveryStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      else setRefreshing(true);
      const [toolsRes, recoveryRes] = await Promise.all([
        api.get<{ tools: ToolStat[] }>(`/admin/agent/tools/stats?days=${days}`),
        api.get<RecoveryStats>('/admin/agent/recovery/stats?days=30'),
      ]);
      setTools(toolsRes.tools);
      setRecovery(recoveryRes);
    } catch (err) {
      if (!silent) {
        addToast(err instanceof Error ? err.message : 'Failed to load tool stats', 'error');
      }
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), 60_000);
    return () => clearInterval(interval);
  }, [days]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <BarChart3 className="w-7 h-7 text-sage-700" />
            <h1 className="text-2xl font-bold text-stone-900">Tool Stats</h1>
          </div>
          <p className="text-stone-500">
            Which of Dippy's tools are pulling their weight and which are costing him tasks.
          </p>
        </div>
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing || isLoading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-stone-300 bg-white text-stone-700 hover:bg-stone-50 disabled:opacity-50"
        >
          <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Recovery efficacy strip */}
      {recovery && recovery.terminal_total > 0 && (
        <Card className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <Shield className="w-5 h-5 text-sage-700" />
            <div>
              <h2 className="font-semibold text-stone-900">Recovery efficacy (30d)</h2>
              <p className="text-xs text-stone-500">
                Of recovery tasks that terminated, how many Dippy resolved without a human.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric
              label="Self-resolved rate"
              value={fmtRate(recovery.self_resolved_rate)}
              valueClass={rateColor(recovery.self_resolved_rate)}
            />
            <Metric label="Self-resolved" value={String(recovery.self_resolved)} />
            <Metric
              label="Escalated"
              value={String(recovery.escalated)}
              valueClass={recovery.escalated > 0 ? 'text-amber-700' : ''}
            />
            <Metric
              label="Still failing"
              value={String(recovery.still_failing)}
              valueClass={recovery.still_failing > 0 ? 'text-red-700' : ''}
            />
          </div>
        </Card>
      )}

      {/* Day filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-stone-600">Window:</span>
        {DAY_OPTIONS.map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={cn(
              'px-3 py-1 text-xs rounded-full border transition-colors',
              days === d
                ? 'bg-sage-700 text-white border-sage-700'
                : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50',
            )}
          >
            {d === 1 ? '24h' : `${d}d`}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Card className="p-6">
          <p className="text-sm text-stone-500">Loading tool stats…</p>
        </Card>
      ) : !tools || tools.length === 0 ? (
        <Card className="p-6">
          <p className="text-sm text-stone-500">
            No tool executions recorded in the last {days === 1 ? '24 hours' : `${days} days`}.
          </p>
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium text-stone-700">Tool</th>
                <th className="text-right px-4 py-2.5 font-medium text-stone-700">Runs</th>
                <th className="text-right px-4 py-2.5 font-medium text-stone-700">Success</th>
                <th className="text-right px-4 py-2.5 font-medium text-stone-700">p50</th>
                <th className="text-right px-4 py-2.5 font-medium text-stone-700">p95</th>
                <th className="text-left px-4 py-2.5 font-medium text-stone-700">Last failure</th>
                <th className="text-right px-4 py-2.5 font-medium text-stone-700">Last used</th>
              </tr>
            </thead>
            <tbody>
              {tools.map((t) => {
                const healthy = (t.success_rate ?? 0) >= 90;
                return (
                  <tr key={t.tool_name} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {healthy ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                        )}
                        <span className="font-mono text-stone-800">{t.tool_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right text-stone-700">{t.invocations}</td>
                    <td className={cn('px-4 py-2.5 text-right font-semibold', rateColor(t.success_rate))}>
                      {fmtRate(t.success_rate)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-stone-600">{fmtMs(t.p50_ms)}</td>
                    <td className="px-4 py-2.5 text-right text-stone-600">{fmtMs(t.p95_ms)}</td>
                    <td className="px-4 py-2.5 max-w-xs">
                      {t.last_failure_at ? (
                        <span
                          className="text-xs text-red-700 truncate block"
                          title={fmtFailureOutput(t.last_failure_output)}
                        >
                          {fmtRelative(t.last_failure_at)}: {fmtFailureOutput(t.last_failure_output)}
                        </span>
                      ) : (
                        <span className="text-xs text-stone-400">none</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right text-stone-500 text-xs">
                      {fmtRelative(t.last_used_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-stone-500">{label}</div>
      <div className={cn('text-2xl font-bold text-stone-900 mt-0.5', valueClass)}>
        {value}
      </div>
    </div>
  );
}
